import { yupResolver } from "@hookform/resolvers/yup";
import { type AlertRootProps, Button, cn, OTPInput, OTPRoot } from "@pharaday/lighthouse";
import { useEffect, useRef } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";

import useLogin from "../../apis/useLogin";
import { StepVerifyEmailSchema } from "./StepVerifyEmail.schema";

export interface StepVerifyEmailProps {
  pendingToken?: string;
  onContinue: () => void;
  onAlert: (alert?: { type: AlertRootProps["type"]; message: string }) => void;
}

const StepVerifyEmail = ({ onContinue, pendingToken }: StepVerifyEmailProps) => {
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const { mutate: login, isSuccess, isPending, data } = useLogin();

  const methods = useForm<StepVerifyEmailSchema>({
    resolver: yupResolver(StepVerifyEmailSchema),
    defaultValues: {
      pendingToken,
      code: "",
    },
  });

  const {
    setValue,
    watch,
    handleSubmit,
    formState: { isDirty, isValid, errors },
    setError,
  } = methods;

  const code = watch("code");

  const handleChange = (value: string) => {
    setValue("code", value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onSubmit: SubmitHandler<StepVerifyEmailSchema> = async (data: StepVerifyEmailSchema) =>
    login({
      code: data.code,
      pendingToken: data.pendingToken,
      type: "with-email-validation",
    });

  useEffect(() => {
    if (!isSuccess) {
      return;
    }

    if (data?.success) {
      onContinue();
    } else {
      setError("code", { type: "server-error" });
    }
  }, [isSuccess, data]);

  const isServerInvalidCode = errors.code?.type === "server-error";

  const handlePaste = () => {
    setTimeout(() => submitButtonRef.current?.click(), 50);
  };

  return (
    <FormProvider {...methods}>
      <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <h3 className="mb-2 text-base font-bold">Verify Email</h3>
          <p className="text-sm">
            We sent a verification code to your email. Please enter the code in the field below.
          </p>
        </div>

        <div>
          <OTPRoot
            className="justify-between"
            onChange={handleChange}
            value={code}
            disabled={isPending}
            isInvalid={isServerInvalidCode}
            onPaste={handlePaste}
          >
            <OTPInput />
            <OTPInput />
            <OTPInput />
            <OTPInput />
            <OTPInput />
            <OTPInput />
          </OTPRoot>

          {isServerInvalidCode && (
            <span className="mt-1 text-sm text-error-50">The verification code that you entered is incorrect</span>
          )}
        </div>

        {/* Hide this feature temporarily due to not having Backend Endpoint yet  */}
        {/* <div>
          <p className="text-sm text-center">
            Didn&apos;t receive the code? <RequestCodeAgainButton onRequest={() => requestSelfSignUpCode(token)} onAlert={onAlert} />
          </p>
        </div> */}

        <Button
          ref={submitButtonRef}
          className={cn("mt-4", isPending && "cursor-wait!")}
          size="lg"
          isLoading={isPending}
          disabled={!isValid || !isDirty}
          type="submit"
        >
          Continue
        </Button>
      </form>
    </FormProvider>
  );
};

export default StepVerifyEmail;
